import { useEffect, useState } from 'react'
import { AdManagementService, type MetaAdLogRead } from '../../client'
import { formatDateTime } from '../../lib/format'
import Modal from '../ui/Modal'

type Fields = Record<string, unknown>

function describeLog({ meta_data }: MetaAdLogRead) {
  const ad = meta_data?.ad as Fields | undefined
  const campaign = meta_data?.campaign as Fields | undefined

  if (ad?.name) return `Created ad "${String(ad.name)}"`
  if (campaign?.id) return `Created ad for campaign ${String(campaign.id)}`
  return 'Ad created'
}

function Details({ title, fields }: { title?: string; fields: [string, unknown][] }) {
  const rows = fields.filter(([, value]) => value != null && value !== '')
  if (rows.length === 0) return null

  return (
    <div className="space-y-1 rounded bg-gray-50 p-2">
      {title && <div className="font-medium text-gray-700">{title}</div>}
      {rows.map(([label, value]) => (
        <div key={label} className="break-words">
          {label}: {String(value)}
        </div>
      ))}
    </div>
  )
}

export default function AuditLogDialog({ brandId, onClose }: { brandId: number; onClose: () => void }) {
  const [logs, setLogs] = useState<MetaAdLogRead[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    AdManagementService.getLogs(brandId)
      .then(setLogs)
      .catch(() => undefined)
      .finally(() => setLoading(false))
  }, [brandId])

  return (
    <Modal title="Activity Log" width="max-w-3xl" onClose={onClose}>
      {loading ? (
        <div className="space-y-4">
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="flex gap-3">
              <div className="mt-2 size-2 shrink-0 animate-pulse rounded-full bg-gray-200" />
              <div className="flex-1 space-y-1">
                <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      ) : logs.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">No activity yet.</p>
      ) : (
        <div className="space-y-4">
          {logs.map((log) => {
            const metadata = log.meta_data ?? {}
            const campaign = (metadata.campaign ?? {}) as Fields
            const adset = (metadata.adset ?? {}) as Fields
            const ad = (metadata.ad ?? {}) as Fields

            return (
              <div key={log.id} className="flex gap-3">
                <div className="mt-2 size-2 shrink-0 rounded-full bg-indigo-400" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-900">{describeLog(log)}</p>
                  <p className="mt-1 text-xs text-gray-400">{formatDateTime(log.created_at)}</p>

                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600">Details</summary>
                    <div className="mt-2 space-y-3 text-xs text-gray-600">
                      <Details
                        fields={[
                          ['Ad Account ID', metadata.ad_account_id],
                          ['Page ID', metadata.page_id],
                        ]}
                      />
                      <Details
                        title="Campaign"
                        fields={[
                          ['ID', campaign.id],
                          ['Name', campaign.name],
                        ]}
                      />
                      <Details
                        title="Ad Set"
                        fields={[
                          ['ID', adset.id],
                          ['Name', adset.name],
                        ]}
                      />
                      <Details
                        title="Ad"
                        fields={[
                          ['ID', ad.id],
                          ['Name', ad.name],
                          ['Creative ID', ad.creative_id],
                          ['Primary Text', ad.primary_text],
                          ['Headline', ad.headline],
                          ['Description', ad.description],
                          ['CTA Type', ad.cta_type],
                          ['CTA Link', ad.cta_link],
                          ['Caption', ad.caption],
                        ]}
                      />
                    </div>
                  </details>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Modal>
  )
}
